import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Shield, Heart, CheckCircle2 } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const About = () => {
  useScrollAnimations();
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Correct Diagnosis',
      description: 'We take the time to identify the root cause of issues, not just treat symptoms. Proper diagnosis leads to lasting repairs.'
    },
    {
      icon: <Award size={32} />,
      title: 'Quality Workmanship',
      description: 'Every repair is completed to the highest standards with attention to detail and guaranteed workmanship.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Long-Term Reliability',
      description: 'We build solutions that last. Our repair-first approach ensures sustainable, cost-effective outcomes.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Trust & Transparency',
      description: 'Honest advice, clear communication, and transparent pricing. No hidden costs or sales pressure.'
    }
  ];

  const expertise = [
    'Multipoint locking systems',
    'Tilt and turn window mechanisms',
    'Tilt and slide door systems',
    'uPVC and aluminium repairs',
    'Complex glazing solutions',
    'Security system upgrades',
    'Alignment and adjustment issues',
    'Emergency locksmith services'
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            About Royal Security
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Based in Romford, serving London and Essex with specialist expertise built on trust, experience, and commitment to quality.
          </p>
        </div>
      </section>

      {/* Main Story */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '60px',
            alignItems: 'start'
          }}>
            <div>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'var(--bg-section-alt)',
                borderRadius: '6px',
                color: 'var(--royal-gold)',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '24px',
                border: '1px solid var(--border-color)'
              }}>
                Our Approach
              </div>
              <h2 className="royal-heading-md">Built Around Care and Consistency</h2>
              <p className="royal-body-lg" style={{ marginBottom: '24px' }}>
                Royal Security was established to provide a different kind of service—one focused on correct diagnosis, quality repairs, and long-term reliability rather than rushed fixes.
              </p>
              <p className="royal-body" style={{ marginBottom: '24px' }}>
                Based in Romford, we serve properties across North, East, South & West London and throughout Essex to Colchester. We work primarily with letting agents, property managers, and landlords who value consistency, clear communication, and professional standards. Our repair-first mindset means we fix the cause of problems, not just the symptoms, resulting in lasting solutions and satisfied clients.
              </p>
              <p className="royal-body">
                Every job receives the same level of care and attention, whether it's an emergency lockout or a complex multipoint locking system repair. We're not the cheapest, but we deliver value through quality workmanship and reliable follow-through.
              </p>
            </div>
            <div>
              <div style={{
                background: 'var(--royal-navy)',
                padding: '48px',
                borderRadius: '12px',
                color: 'var(--royal-white)'
              }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '24px',
                  color: 'var(--royal-gold)'
                }}>
                  What Sets Us Apart
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Specialist knowledge of complex systems',
                    'Repair-first, not replace-first approach',
                    'Professional reporting and documentation',
                    'Transparent pricing with no surprises',
                    'Fully insured with guaranteed work',
                    'Emergency call-out available',
                    'Long-term partnerships, not one-off jobs',
                    'Calm, professional service every time'
                  ].map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '16px',
                      fontSize: '0.95rem',
                      lineHeight: '1.6'
                    }}>
                      <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Our Core Values</h2>
            <p className="royal-body-lg">
              These principles guide every decision we make and every job we complete.
            </p>
          </div>

          <div className="royal-grid royal-grid-2">
            {values.map((value, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '24px',
                padding: '40px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{
                  flexShrink: 0,
                  width: '64px',
                  height: '64px',
                  background: 'var(--royal-navy)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--royal-gold)'
                }}>
                  {value.icon}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: 'var(--royal-navy)'
                  }}>
                    {value.title}
                  </h3>
                  <p className="royal-body">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="royal-heading-lg" style={{ marginBottom: '24px' }}>
              Specialist Expertise
            </h2>
            <p className="royal-body-lg" style={{ marginBottom: '48px' }}>
              We focus on complex systems that require proper diagnosis and specialist knowledge.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '20px',
              textAlign: 'left'
            }}>
              {expertise.map((item, index) => (
                <div key={index} style={{
                  padding: '20px',
                  background: 'var(--bg-section-alt)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: 'var(--royal-gold)',
                    borderRadius: '50%',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Standards */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="royal-heading-lg" style={{ marginBottom: '32px' }}>
              Trust & Standards
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '32px',
              marginTop: '48px'
            }}>
              <div style={{
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '2px solid var(--royal-gold)'
              }}>
                <Shield size={48} style={{ color: 'var(--royal-gold)', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Fully Insured
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Comprehensive insurance coverage for all work
                </p>
              </div>
              <div style={{
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '2px solid var(--royal-gold)'
              }}>
                <Award size={48} style={{ color: 'var(--royal-gold)', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Guaranteed Work
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Quality workmanship guaranteed
                </p>
              </div>
              <div style={{
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '2px solid var(--royal-gold)'
              }}>
                <CheckCircle2 size={48} style={{ color: 'var(--royal-gold)', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Professional Standards
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  High standards on every job
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'var(--royal-white)'
          }}>
            Work With a Trusted Partner
          </h2>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Experience the difference of working with specialists who care about quality and reliability.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Get in Touch
            </Link>
            <Link to="/services" className="royal-btn-light">
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
